import React, { useEffect, useRef, useState } from 'react';
import { Flex, Box, useToast } from '@chakra-ui/react';
import { useDispatch, useTrackedState } from '../config/store';
import InterviewStatusFilterMenu from '../components/interviews/InterviewStatusFilterMenu';
import InterviewMainFilter from '../components/interviews/InterviewMainFilter';
import InterviewSearchBar from '../components/interviews/InterviewSearchBar';
import InterviewList from '../components/interviews/InterviewList';
import LoadingInterviews from '../components/interviews/LoadingInterviews';
import NoInterviews from '../components/interviews/NoInterviews';
import { sendAmplitudeData, setAmplitudeUserId } from '../utils/amplitudeUtils';
import InterviewsError from '../components/interviews/InterviewsError';

export default function Interviews() {
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [interviewStatusScheduledFilter, setInterviewStatusScheduledFilter] = useState(false);
  const [interviewStatusCompletedFilter, setInterviewStatusCompletedFilter] = useState(false);
  const [interviewStatusReportPendingFilter, setInterviewStatusReportPendingFilter] = useState(false);
  const [interviewStatusMissingVideoFilter, setInterviewStatusMissingVideoFilter] = useState(false);

  const [querySearch, setQuerySearch] = useState('');

  const state = useTrackedState();
  const dispatch = useDispatch();
  const [interviewStatusFilter, setInterviewStatusFilter] = useState('All Interviews');

  const toast = useToast();
  const displayingToast = useRef(false); // useRef to prevent multiple toasts from being displayed due to useEffect

  const getInterviewsPaginated = () => {
    dispatch({
      type: 'GET_INTERVIEWS_PAGINATION_PARAMS',
      token: state.user.token,
      page: state?.pagination?.page,
      interviewStatusFilter: state.interviewStatusFilter,
    });
  };

  useEffect(() => {
    if (state && state.user) {
      setInterviewStatusFilter(state.interviewStatusFilter);
      setStartDateFilter(state.dateSearchStart);
      setEndDateFilter(state.dateSearchEnd);
      setInterviewStatusScheduledFilter(state.interviewScheduled);
      setInterviewStatusCompletedFilter(state.interviewCompleted);
      setInterviewStatusReportPendingFilter(state.interviewReportPending);
      setInterviewStatusMissingVideoFilter(state.missingVideo);
      setQuerySearch(state.querySearch);

      if (!state.interviews) {
        if (state?.pagination?.page) {
          getInterviewsPaginated();
        }
      }
    }
  }, [state.user, state?.pagination?.page]);

  useEffect(() => {
    if (state?.interviewSubmitStatus === 'record updated' && !displayingToast.current) {
      // First check if toast is already displaying
      if (displayingToast.current) displayingToast.current = false;
      else {
        toast({
          position: 'bottom-right',
          isClosable: true,
          title: 'Success!',
          description: 'Your information was submitted!',
          status: 'success',
        });

        displayingToast.current = true;
      }

      dispatch({ type: 'SET_INTERVIEW_SUBMIT_STATUS', interviewSubmitStatus: null });
      getInterviewsPaginated();
    }
  }, [state.interviewSubmitStatus]);

  const handleApplySearch = () => {
    setInterviewStatusFilter(undefined);

    dispatch({
      type: 'GET_INTERVIEWS_PAGINATION_PARAMS',
      token: state.user.token,
      page: 0,
      interviewStatusFilter: undefined,
      interviewScheduled: interviewStatusScheduledFilter,
      interviewCompleted: interviewStatusCompletedFilter,
      interviewReportPending: interviewStatusReportPendingFilter,
      missingVideo: interviewStatusMissingVideoFilter,
      dateSearchStart: startDateFilter,
      dateSearchEnd: endDateFilter,
      querySearch,
    });

    const interviewsFiltered = () => {
      const interviewStatus = [];
      if (interviewStatusCompletedFilter) {
        interviewStatus.push('Completed');
      }
      if (interviewStatusReportPendingFilter) {
        interviewStatus.push('Pending');
      }
      if (interviewStatusScheduledFilter) {
        interviewStatus.push('Scheduled');
      }
      if (interviewStatusMissingVideoFilter) {
        interviewStatus.push('Missing Video');
      }
      return interviewStatus;
    };
    setAmplitudeUserId(state.user.email);
    sendAmplitudeData('FILTER_INTERVIEWS', {
      searchText: querySearch,
      dateFrom: startDateFilter,
      dateTo: endDateFilter,
      interviewStatus: interviewsFiltered(),
    });
  };

  const handleEraseAllFilters = () => {
    setInterviewStatusFilter(undefined);
    setInterviewStatusScheduledFilter(false);
    setInterviewStatusCompletedFilter(false);
    setInterviewStatusReportPendingFilter(false);
    setInterviewStatusMissingVideoFilter(false);
    setStartDateFilter('');
    setEndDateFilter('');

    dispatch({
      type: 'RESETTED_INTERVIEWS_FILTERS',
    });

    dispatch({
      type: 'GET_INTERVIEWS_PAGINATION_PARAMS',
      token: state.user.token,
      page: 1,
    });
  };
  // Initial state is null, while fetching interviews
  if (state.interviews === null) return <LoadingInterviews />;
  const newInterviewer =
    !state.interviews?.length &&
    state.pending === false &&
    !state.querySearch?.length &&
    !state.dateSearchStart?.length &&
    !state.dateSearchEnd?.length &&
    !state.interviewScheduled &&
    !state.interviewCompleted &&
    !state.interviewReportPending &&
    !state.missingVideo &&
    state.interviewStatusFilter === 'All Interviews';
  return (
    // Otherwise show interviews list with filters and search

    <Box maxW='1366px' px='20px' mt='24px' pb='32px'>
      {!newInterviewer && (
        <Flex justifyContent='space-between' alignItems='center' mb='24px'>
          <InterviewStatusFilterMenu
            interviewStatusFilter={state.interviewStatusFilter}
            setInterviewStatusFilter={setInterviewStatusFilter}
          />
          <Flex>
            <InterviewSearchBar setQuerySearch={setQuerySearch} handleApplySearch={handleApplySearch} />
            <InterviewMainFilter
              interviewStatusScheduledFilter={interviewStatusScheduledFilter}
              setInterviewStatusScheduledFilter={setInterviewStatusScheduledFilter}
              interviewStatusCompletedFilter={interviewStatusCompletedFilter}
              setInterviewStatusCompletedFilter={setInterviewStatusCompletedFilter}
              interviewStatusReportPendingFilter={interviewStatusReportPendingFilter}
              setInterviewStatusReportPendingFilter={setInterviewStatusReportPendingFilter}
              interviewStatusMissingVideoFilter={interviewStatusMissingVideoFilter}
              setInterviewStatusMissingVideoFilter={setInterviewStatusMissingVideoFilter}
              startDateFilter={startDateFilter}
              setStartDateFilter={setStartDateFilter}
              endDateFilter={endDateFilter}
              setEndDateFilter={setEndDateFilter}
              handleApplySearch={handleApplySearch}
              handleEraseAllFilters={handleEraseAllFilters}
            />
          </Flex>
        </Flex>
      )}

      {state.error && <InterviewsError />}
      {
        // If interviews return empty array and we fetch All, then there's no interviews
        !state.interviews?.length && state.pending === false ? (
          <NoInterviews newInterviewer={newInterviewer} />
        ) : (
          <InterviewList interviewStatusFilter={interviewStatusFilter} />
        )
      }
    </Box>
  );
}
